import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { HOME_SLIDER } from "../../utils/constants";

// Özel CSS stillerini ekleyin - bunları ayrı bir CSS dosyasına da koyabilirsiniz
const styles = {
  "@keyframes slideInRight": {
    "0%": { transform: "translateX(100%)" },
    "100%": { transform: "translateX(0)" }
  },
  "@keyframes slideInLeft": {
    "0%": { transform: "translateX(-100%)" },
    "100%": { transform: "translateX(0)" }
  },
  "@keyframes slideOutRight": {
    "0%": { transform: "translateX(0)" },
    "100%": { transform: "translateX(100%)" }
  },
  "@keyframes slideOutLeft": {
    "0%": { transform: "translateX(0)" },
    "100%": { transform: "translateX(-100%)" }
  }
};

const HomeSlider = ({ onViewProducts }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);
  const [direction, setDirection] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef(null);
  const sliderRef = useRef(null);
  const startXRef = useRef(0);
  const isDraggingRef = useRef(false);

  // Otomatik geçiş için zamanlayıcı
  const startAutoSlide = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (!isAnimating) {
        goToNextSlide();
      }
    }, 5000);
  };

  // Geçiş yönetimi
  const goToSlide = (index, dir) => {
    if (isAnimating || index === activeIndex) return;
    setIsAnimating(true);
    setPrevIndex(activeIndex);
    setDirection(dir);
    setActiveIndex(index);
    
    // Animasyon süresi kadar bekleyip isAnimating'i sıfırla
    setTimeout(() => {
      setIsAnimating(false);
    }, 600);
  };

  const goToNextSlide = () => {
    const newIndex = (activeIndex + 1) % HOME_SLIDER.length;
    goToSlide(newIndex, "next");
  };

  const goToPrevSlide = () => {
    const newIndex = (activeIndex - 1 + HOME_SLIDER.length) % HOME_SLIDER.length;
    goToSlide(newIndex, "prev");
  };

  // Başlangıçta ve slide değişince otomatik geçişi başlat
  useEffect(() => {
    startAutoSlide();
    return () => clearTimeout(timeoutRef.current);
  }, [activeIndex, isAnimating]);

  // Özel CSS keyframe animasyonlarını enjekte et
  useEffect(() => {
    // Stil elemanı oluştur
    const styleEl = document.createElement('style');
    styleEl.type = 'text/css';
    
    // Tüm keyframe animasyonları ekle
    let css = "";
    for (const [key, value] of Object.entries(styles)) {
      if (key.startsWith('@keyframes')) {
        css += `${key} {`;
        for (const [percent, style] of Object.entries(value)) {
          css += `${percent} {`;
          for (const [prop, val] of Object.entries(style)) {
            css += `${prop}: ${val};`;
          }
          css += `}`;
        }
        css += `}`;
      }
    }
    
    styleEl.appendChild(document.createTextNode(css));
    document.head.appendChild(styleEl);
    
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  // Touch ve Mouse olayları
  const handleTouchStart = (e) => {
    if (isAnimating) return;
    clearTimeout(timeoutRef.current);
    startXRef.current = e.touches ? e.touches[0].clientX : e.clientX;
    isDraggingRef.current = true;
  };

  const handleTouchMove = (e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
  };

  const handleTouchEnd = (e) => {
    if (!isDraggingRef.current) return;
    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const diffX = endX - startXRef.current;
    const threshold = window.innerWidth * 0.15; // Ekran genişliğinin %15'i
    
    if (diffX > threshold) {
      goToPrevSlide(); // Sağa doğru sürükleme - önceki slide
    } else if (diffX < -threshold) {
      goToNextSlide(); // Sola doğru sürükleme - sonraki slide
    }
    
    isDraggingRef.current = false;
    startAutoSlide();
  };

  const handleMouseDown = (e) => {
    handleTouchStart(e);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    handleTouchMove(e);
  };

  const handleMouseUp = (e) => {
    handleTouchEnd(e);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Sayfa navigasyonu
  const handleNavigation = (path) => {
    if (isDraggingRef.current || isAnimating) return;
    
    if (path === "/products" && onViewProducts) onViewProducts();
    else navigate(path);
  };

  // Slide için Animasyon Kodu
  const getSlideAnimation = (index) => {
    // Aktif slide
    if (index === activeIndex) {
      return {
        zIndex: 2,
        animation: direction === "next" 
          ? "slideInRight 0.5s forwards" 
          : "slideInLeft 0.5s forwards"
      };
    } 
    // Önceki slide
    else if (index === prevIndex) {
      return {
        zIndex: 1,
        animation: direction === "next" 
          ? "slideOutLeft 0.5s forwards" 
          : "slideOutRight 0.5s forwards"
      };
    }
    // Diğer slidelar
    return {
      zIndex: 0,
      display: "none"
    };
  };

  return (
    <Box
      ref={sliderRef}
      sx={{
        height: "100vh",
        overflow: "hidden",
        mt: "-70px",
        position: "relative",
        cursor: isDraggingRef.current ? "grabbing" : "grab",
        touchAction: "none", // Tüm doğal dokunmatik hareketleri devre dışı bırak
        userSelect: "none"
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
    >
      {/* Arkaplan için kesinlikle beyaz olmamasını sağlar */}
      <Box 
        sx={{ 
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#000", // Siyah arkaplan
          zIndex: 0
        }} 
      />

      {/* Tüm slidelar */}
      {HOME_SLIDER.map((slide, idx) => {
        const animStyle = getSlideAnimation(idx);
        
        return (
          <Box
            key={idx}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${slide.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              zIndex: animStyle.zIndex,
              display: animStyle.display === "none" ? "none" : "flex",
              alignItems: "center",
              animation: animStyle.animation,
              transformOrigin: "center center",
              WebkitBackfaceVisibility: "hidden",
              backfaceVisibility: "hidden",
              WebkitPerspective: 1000,
              perspective: 1000,
              WebkitTransformStyle: "preserve-3d",
              transformStyle: "preserve-3d",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              willChange: "transform"
            }}
          >
            {/* Karartma overlay */}
            <Box
              sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                top: 0,
                left: 0,
              }}
            />

            {/* İçerik - sadece aktif slide'da gösterilir */}
            {idx === activeIndex && (
              <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2 }}>
                <Box
                  sx={{
                    maxWidth: { xs: "100%", md: "60%" },
                    px: { xs: 2, md: 0 },
                  }}
                >
                  <motion.div
                    key={`title-${activeIndex}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <Typography
                      variant="h1"
                      sx={{
                        color: "white",
                        fontWeight: 700,
                        mb: 2,
                        fontSize: { xs: "2.5rem", sm: "3rem", md: "4rem" },
                      }}
                    >
                      {i18n.language === "tr"
                        ? slide.title.tr
                        : slide.title.en}
                    </Typography>
                  </motion.div>
                  
                  <motion.div
                    key={`desc-${activeIndex}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: "white",
                        fontWeight: 400,
                        mb: 4,
                        fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                      }}
                    >
                      {i18n.language === "tr"
                        ? slide.description.tr
                        : slide.description.en}
                    </Typography>
                  </motion.div>
                  
                  <motion.div
                    key={`buttons-${activeIndex}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        onClick={() => handleNavigation("/quote")}
                      >
                        {t("nav.getQuote")}
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => handleNavigation("/products")}
                        sx={{ color: "white", borderColor: "white" }}
                      >
                        {t("products.viewAll")}
                      </Button>
                    </Box>
                  </motion.div>
                </Box>
              </Container>
            )}
          </Box>
        );
      })}

      {/* Sürükleme ipucu */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        sx={{
          position: "absolute",
          bottom: isMobile ? 70 : 80,
          width: "100%",
          textAlign: "center",
          zIndex: 10,
          pointerEvents: "none",
        }}
      >
        <Typography 
          variant="body2"
          sx={{ 
            color: "white", 
            backgroundColor: "rgba(0,0,0,0.6)",
            py: 1,
            px: 2,
            borderRadius: 2,
            display: "inline-block",
          }}
        >
          {i18n.language === "tr" ? "Sağa veya sola kaydırın" : "Swipe left or right"}
        </Typography>
      </Box>

      {/* Nokta göstergeleri */}
      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          width: "100%",
          textAlign: "center",
          zIndex: 10,
        }}
      >
        {HOME_SLIDER.map((_, idx) => (
          <Box
            key={idx}
            component={motion.span}
            whileHover={{ scale: 1.3 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => {
              if (isAnimating) return;
              const dir = idx > activeIndex ? "next" : "prev";
              goToSlide(idx, dir);
            }}
            sx={{
              display: "inline-block",
              width: idx === activeIndex ? 16 : 12,
              height: idx === activeIndex ? 16 : 12,
              borderRadius: "50%",
              mx: 0.5,
              backgroundColor: idx === activeIndex ? "white" : "rgba(255,255,255,0.5)",
              cursor: isAnimating ? "default" : "pointer",
              transition: "all 0.3s ease",
              boxShadow: idx === activeIndex ? "0 0 8px rgba(255,255,255,0.8)" : "none"
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default HomeSlider;